import React from 'react';
import { View, StyleSheet, Animated, Dimensions, Platform } from 'react-native';
import { useTheme } from '../../../hooks/useTheme';
import { CardLayout } from './types';

const { width } = Dimensions.get('window');

interface SkeletonProps {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: any;
}

const SkeletonBox: React.FC<SkeletonProps> = ({
  width: boxWidth,
  height,
  borderRadius = 4,
  style,
}) => {
  const { colors } = useTheme();
  const animatedValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: false,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, []);

  const backgroundColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [colors.border, colors.secondary + '40'],
  });

  return (
    <Animated.View
      style={[
        {
          width: boxWidth,
          height,
          backgroundColor,
          borderRadius,
        },
        style,
      ]}
    />
  );
};

// Vertical Card Skeleton (matches VerticalMoodboardCard)
const VerticalCardSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const screenWidth = Dimensions.get('window').width;
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  const baseWidth = Math.min(500, screenWidth * 0.9);

  return (
    <View
      style={[
        styles.verticalCard,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      {/* Image Section */}
      <View
        style={[
          styles.verticalImageContainer,
          { width: isMobile ? '100%' : baseWidth },
        ]}
      >
        <SkeletonBox
          width="100%"
          height={isMobile ? 300 : 400}
          borderRadius={0}
        />
      </View>

      {/* Details Section */}
      <View style={styles.verticalDetails}>
        {/* Header with User Info */}
        <View style={styles.verticalHeader}>
          <View style={styles.userInfoSection}>
            <SkeletonBox width={32} height={32} borderRadius={16} />
            <View style={styles.userTextInfo}>
              <SkeletonBox width={100} height={14} borderRadius={7} />
              <SkeletonBox
                width={60}
                height={10}
                borderRadius={5}
                style={{ marginTop: 4 }}
              />
            </View>
          </View>
          <SkeletonBox width={50} height={20} borderRadius={10} />
        </View>

        {/* Title and Description */}
        <SkeletonBox
          width="90%"
          height={16}
          borderRadius={8}
          style={{ marginTop: 8 }}
        />
        <SkeletonBox
          width="70%"
          height={14}
          borderRadius={7}
          style={{ marginTop: 6 }}
        />

        {/* Color Palette */}
        <View style={styles.colorPaletteSkeleton}>
          {[1, 2, 3, 4, 5].map((_, index) => (
            <SkeletonBox
              key={index}
              width={30}
              height={30}
              borderRadius={15}
              style={{ marginRight: 6 }}
            />
          ))}
        </View>

        {/* Footer Actions */}
        <View style={styles.verticalFooter}>
          <View style={styles.actionsRow}>
            <SkeletonBox width={60} height={24} borderRadius={12} />
            <SkeletonBox width={60} height={24} borderRadius={12} />
            <SkeletonBox width={60} height={24} borderRadius={12} />
          </View>
        </View>
      </View>
    </View>
  );
};

// Thumbnail Card Skeleton (matches ThumbnailCard)
const ThumbnailCardSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const screenWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web' && screenWidth >= 768;
  const cardWidth = isWeb ? '33.333%' : '100%';

  return (
    <View style={[styles.thumbnailCardContainer, { width: cardWidth }]}>
      <View
        style={[
          styles.thumbnailCard,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        {/* Image */}
        <View style={styles.thumbnailImageContainer}>
          <SkeletonBox
            width="100%"
            height={isWeb ? 250 : 300}
            borderRadius={12}
          />
        </View>

        {/* Overlay with User Info */}
        <View style={styles.thumbnailOverlay}>
          <View style={styles.thumbnailUserInfo}>
            <SkeletonBox width={20} height={20} borderRadius={10} />
            <View style={styles.thumbnailUserDetails}>
              <SkeletonBox width={80} height={10} borderRadius={5} />
              <SkeletonBox
                width={50}
                height={8}
                borderRadius={4}
                style={{ marginTop: 2 }}
              />
            </View>
          </View>
        </View>

        {/* Title */}
        <View style={styles.thumbnailTitleContainer}>
          <SkeletonBox width="85%" height={12} borderRadius={6} />
        </View>
      </View>
    </View>
  );
};

interface MoodBoardListSkeletonProps {
  count?: number;
  layout?: CardLayout;
}

const MoodBoardListSkeleton: React.FC<MoodBoardListSkeletonProps> = ({
  count = 3,
  layout = 'vertical',
}) => {
  const screenWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web' && screenWidth >= 768;

  if (layout === 'thumbnail') {
    return (
      <View style={styles.thumbnailGridContainer}>
        {Array.from({ length: count }).map((_, index) => (
          <ThumbnailCardSkeleton key={index} />
        ))}
      </View>
    );
  }

  // Vertical or horizontal layout
  return (
    <View style={styles.container}>
      {Array.from({ length: count }).map((_, index) => (
        <VerticalCardSkeleton key={index} />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Platform.OS === 'web' ? 0 : 0,
    paddingBottom: Platform.OS === 'web' ? 100 : 120,
    paddingTop: Platform.OS === 'web' ? 6 : 0,
    width: '100%',
    alignItems: 'center',
  },
  // Vertical Card Styles
  verticalCard: {
    borderRadius: 0,
    marginBottom: Platform.OS === 'web' ? 20 : 0,
    borderWidth: 0,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 500 : '100%',
    alignSelf: 'center',
    overflow: 'hidden',
  },
  verticalImageContainer: {
    width: '100%',
    overflow: 'hidden',
  },
  verticalDetails: {
    padding: Platform.OS === 'web' ? 16 : 12,
    backgroundColor: 'transparent',
  },
  verticalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  userInfoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  userTextInfo: {
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },
  colorPaletteSkeleton: {
    flexDirection: 'row',
    marginTop: 12,
    alignItems: 'center',
  },
  verticalFooter: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  // Thumbnail Card Styles
  thumbnailGridContainer: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    flexWrap: Platform.OS === 'web' ? 'wrap' : 'nowrap',
    paddingHorizontal: Platform.OS === 'web' ? 8 : 0,
    paddingTop: Platform.OS === 'web' ? 8 : 0,
    paddingBottom: Platform.OS === 'web' ? 8 : 16,
    gap: Platform.OS === 'web' ? 12 : 0,
    width: '100%',
  },
  thumbnailCardContainer: {
    marginBottom: Platform.OS === 'web' ? 12 : 1,
    alignSelf: 'flex-start',
  },
  thumbnailCard: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 0,
    position: 'relative',
  },
  thumbnailImageContainer: {
    width: '100%',
    aspectRatio: 1,
    overflow: 'hidden',
  },
  thumbnailOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: Platform.OS === 'web' ? 3 : 6,
  },
  thumbnailUserInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  thumbnailUserDetails: {
    flex: 1,
  },
  thumbnailTitleContainer: {
    padding: Platform.OS === 'web' ? 6 : 8,
  },
});

export default MoodBoardListSkeleton;
